import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Paper, Popper, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobalState } from '../../../context/GlobalState';
import CurlyArrow from './CurlyArrow';
import { walkthroughColors, walkthroughPaperSx } from './walkthroughStyle';

const DISMISSED_KEY = 'tractech_management_hint_dismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

/** Pages where the hint makes no sense: the panel itself, login and the public company-creation form. */
const HIDDEN_ON = ['/management', '/login', '/onboarding'];

type Props = {
  /** The ☰ menu button in the top bar; null when a page replaces it (e.g. with a back button). */
  anchorEl: HTMLElement | null;
  /** True while the side menu is open (the user found it). */
  hidden: boolean;
};

/**
 * Outside the management panel, tells an organisation admin who hasn't finished the walkthrough that
 * the panel is opened from the menu, with a curly arrow pointing at the ☰ button. Closing it hides it
 * for the rest of the browser session.
 */
const ManagementPanelHint = ({ anchorEl, hidden }: Props) => {
  const { t } = useTranslation();
  const { user } = useGlobalState();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(readDismissed);

  const show =
    !!anchorEl &&
    !hidden &&
    !dismissed &&
    !!user &&
    !user.isSuperUser &&
    user.onboardingStatus !== 'COMPLETED' &&
    !HIDDEN_ON.some((path) => location.pathname.startsWith(path));

  if (!show) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Not remembered across reloads when storage is unavailable.
    }
  };

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement='bottom-start'
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 1, maxWidth: 'calc(100vw - 32px)' }}
      modifiers={[
        { name: 'offset', options: { offset: [12, 0] } },
        { name: 'flip', enabled: false },
        { name: 'preventOverflow', options: { padding: 16 } }
      ]}
      data-testid='management-panel-hint'
    >
      {/* Curly arrow from the note up to the menu button. */}
      <CurlyArrow />
      <Paper elevation={6} sx={{ ...walkthroughPaperSx, p: 2, pr: 1, width: 320, maxWidth: '100%', display: 'flex' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('managementPanel.walkthrough.hint.title')}
          </Typography>
          <Typography variant='body2'>{t('managementPanel.walkthrough.hint.text')}</Typography>
        </Box>
        <IconButton
          size='small'
          onClick={dismiss}
          aria-label={t('managementPanel.walkthrough.hint.close')}
          sx={{ alignSelf: 'flex-start', color: walkthroughColors.text }}
          data-testid='management-panel-hint-close'
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </Paper>
    </Popper>
  );
};

export default ManagementPanelHint;
